import {AxiosError} from 'axios';
import {useState, useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import moment from 'moment';
import {ImageListType, ImageType} from 'react-images-uploading';
import {HttpError, httpHeaderWithFormContentType} from '../../../../../common/hooks/ApiClient';
import {useAuthedMutation} from '../../../../../common/hooks/QueryHooks';
import {Store} from '../../../../auth/common/Types';
import useFollowers from './useFollowers';
import {removeUndefined} from '../../../../../common/helpers';
import {routes} from '../../../../../routing/routes';
import {
	CampaignCreateData,
	CampaignCreateRequest,
	CampaignCreateResponse,
	Genre,
	Platforms,
	Service,
	SocialPostType,
	SocialPostTypes,
} from '../types/Types';
import {BusinessHour, Day, Days} from '../../../../../common/types/BusinessTimes';
import {Gender} from '../../../../../common/types/Gender';
import {useAuth} from '../../../../auth';
import {useLayout} from '../../../../../common/components/layout/LayoutProvider';
import useCampaignSlots from '../../details/hooks/useCampaignSlots';

const useCampaignCreate = () => {
	const {state} = useLocation();
	const {currentUser} = useAuth();
	const {setShouldShowNavbarAction} = useLayout();
	const [genre, setGenre] = useState<Genre | undefined>(undefined);
	const [service, setService] = useState<Service | undefined>(undefined);
	const [socialPostTypes, setSocialPostTypes] = useState<SocialPostType[]>([]);
	const [store, setStore] = useState<Store | undefined>();
	const [title, setTitle] = useState<string | undefined>();
	const [thumb, setThumb] = useState<ImageType | undefined>(undefined);
	const [thumbUrl, setThumbUrl] = useState<string | undefined>(undefined);
	const [images, setImages] = useState<ImageListType>([]);
	const [summary, setSummary] = useState<string | undefined>();
	const [charge, setCharge] = useState<number | undefined>();
	const [hours, setHours] = useState<BusinessHour | undefined>();
	const [holidays, setHolidays] = useState<Day[]>([Days.friday]);
	const [treatmentTime, setTreatmentTime] = useState<number | undefined>();
	const [slots, setSlots] = useState<number | undefined>(0);
	const [genders, setGenders] = useState<Gender[]>([]);
	const {followers, updateFollowers} = useFollowers({socialPostTypes, charge});
	const [hashtags, setHashtags] = useState<string[]>([]);
	const [companions, setCompanions] = useState<number | undefined>();
	const [success, setSuccess] = useState<boolean>(false);
	const [id, setId] = useState<number | undefined>();
	const [error, setError] = useState<string | undefined>();
	const [totalSlots, setTotalSlots] = useState<number>(0);
	const [hasCompanion, setHasCompanion] = useState<boolean>(true);

	const [increaseModalOpen, setIncreaseModalOpen] = useState<boolean>(false);
	const navigate = useNavigate();

	const {mutate: save, isLoading: isSaving} = useAuthedMutation<CampaignCreateResponse>('/enterpriser/campaign/draft');
	const {mutate: update, isLoading: isUpdating} = useAuthedMutation<CampaignCreateResponse>(
		`/enterpriser/campaign/${id}/draft`,
	);
	const {data: campaignSlots, refetch} = useCampaignSlots();

	const isLoading = isSaving || isUpdating;

	useEffect(() => {
		if (state?.option === 'edit' || state?.option === 'copy') {
			if (state?.option === 'edit') {
				setId(state.id);
			}

			// Basic Info
			setGenre(state?.genre);
			setSocialPostTypes(state?.socialPostTypes);

			if (state?.service) {
				setService({key: state?.service, value: state?.service});
			}

			// Address
			const stores = currentUser?.enterpriser?.stores ?? [];
			if (stores) {
				const storeInfo = stores?.find((s) => s.id === state?.storeId);
				setStore(storeInfo as Store);
			}

			// Details
			setTitle(state?.title);
			setSocialPostTypes(state?.socialPostTypes?.types);
			if (state?.imageUrl) {
				setThumb({dataURL: state?.imageUrl});
				setThumbUrl(state.imageUrl);
			}
			if (state?.imageList) {
				const photos = (state?.imageList ?? []).map((image: string) => ({dataURL: image})) ?? [];
				setImages(photos);
			}
			setSummary(state?.description === null || state?.description === 'null' ? '' : state.description);
			if (state?.priceRange) {
				setCharge(state?.priceRange?.maxPrice);
			}

			setHours({start: moment(state?.businessHour?.start, 'HH:mm'), end: moment(state?.businessHour?.end, 'HH:mm')});
			setHolidays(state?.holidays?.days);
			if (state?.treatmentTime) {
				setTreatmentTime(state?.treatmentTime);
			}
			setSlots(state?.totalSlot);

			// influencer
			setGenders(state?.genders?.genderList);
			if (
				(state?.socialPostTypes?.types?.includes(SocialPostTypes.igPost) > -1 ||
					state?.socialPostTypes?.types?.includes(SocialPostTypes.igStory) > -1) &&
				state?.minInstagramCount
			) {
				updateFollowers('instagram' as Platforms.ig, state?.minInstagramCount);
			}
			if (state?.socialPostTypes?.types?.includes(SocialPostTypes.tiktok) > -1 && state?.minTiktokCount) {
				updateFollowers('tiktok' as Platforms.tiktok, state?.minTiktokCount);
			}

			// post
			if (state?.hashtags) {
				setHashtags(state?.hashtags);
			}
			if (state?.companionCount) {
				setCompanions(state?.companionCount);
				setHasCompanion(true);
			}
			setShouldShowNavbarAction(false);
		}
	}, [state, charge, currentUser, updateFollowers, setShouldShowNavbarAction]);

	const data: CampaignCreateData = {
		genre,
		setGenre,
		service,
		setService,
		socialPostTypes,
		setSocialPostTypes,
		store,
		setStore,
		title,
		setTitle,
		thumb,
		setThumb,
		thumbUrl,
		setThumbUrl,
		images,
		setImages,
		summary,
		setSummary,
		charge,
		setCharge,
		hours,
		setHours,
		holidays,
		setHolidays,
		treatmentTime,
		setTreatmentTime,
		slots,
		setSlots,
		genders,
		setGenders,
		followers,
		updateFollowers,
		hashtags,
		setHashtags,
		companions,
		setCompanions,
		id,
		setId,
		success,
		setSuccess,
		increaseModalOpen,
		setIncreaseModalOpen,
		totalSlots,
		hasCompanion,
		setHasCompanion,
		refetch,
	};

	const getImage = (image: ImageType | undefined): File | string | undefined => {
		if (!image) return undefined;
		if (image.file) return image.file;
		return image.dataURL;
	};

	const getImages = () => {
		return images.filter((image) => image.file !== undefined).map((image) => image.file) as File[];
	};

	const getImageList = () => {
		return images.filter((image) => image.file === undefined).map((image) => image.dataURL) as string[];
	};

	const apiData = removeUndefined<CampaignCreateRequest>({
		genre: genre?.key,
		service: service?.key,
		socialPostTypes,
		storeId: store?.id,
		title,
		...(thumb?.file && {thumb: getImage(thumb)}),
		...(thumb?.file || {thumbUrl}),
		images: getImages().length > 0 ? getImages() : undefined,
		imgList: getImageList().length > 0 ? getImageList() : undefined,
		summary,
		charge,
		startTime: hours?.start.format('HH:mm'),
		endTime: hours?.end.format('HH:mm'),
		holidays,
		slots,
		treatmentTime,
		genders,
		instagramCount: followers?.[Platforms.ig],
		tiktokCount: followers?.[Platforms.tiktok],
		hashtags,
		companions,
	});

	useEffect(() => {
		const currentSlots = (slots ?? 0) as number;
		const totalInitialSlot = (campaignSlots?.totalInitialSlot ?? 0) as number;
		const totalSlotsNumber = currentSlots - totalInitialSlot + totalInitialSlot;
		const isIncrease = totalSlotsNumber > ((campaignSlots?.slots ?? 0) as number);
		if (campaignSlots && isIncrease && state?.status !== 'Published') {
			setTotalSlots(totalSlotsNumber);
		} else {
			setTotalSlots(0);
		}
	}, [slots, campaignSlots, state]);

	const saveAsDraft = async (info: string) => {
		const currentSlots = (slots ?? 0) as number;
		const totalInitialSlot = (campaignSlots?.totalInitialSlot ?? 0) as number;
		const totalSlotsNumber = currentSlots - totalInitialSlot + totalInitialSlot;
		const isIncrease = totalSlotsNumber > ((campaignSlots?.slots ?? 0) as number);
		if (info === 'draft' && campaignSlots && slots && isIncrease && state?.status !== 'Published') {
			setIncreaseModalOpen(true);
			setTotalSlots(totalSlotsNumber);
			return;
		}
		try {
			if (id === undefined) {
				const res = await save({data: apiData, headers: httpHeaderWithFormContentType});
				setId(res.id);

				if (info === 'draft' && res) {
					setSuccess(true);
				} else if (res.id) {
					navigate(routes.campaign.info.replace(':id', `details/${res.id}`));
				}
			} else {
				const res = await update({data: apiData, headers: httpHeaderWithFormContentType});
				if (res && info === 'draft') {
					setSuccess(true);
				}
			}
		} catch (e) {
			if (e instanceof HttpError || e instanceof AxiosError) {
				if (e.message === 'Exceeds Enterpriser Slot.') {
					navigate(routes.accountSettingsRoutes.plan);
					setError(e.message);
				}
			}
		}
	};

	return {id, data, isLoading, error, saveAsDraft, setId, success, setSuccess};
};

export default useCampaignCreate;
